import { JobResponse, jobResponseSchema } from '@parkchirp/reporting';
import { ErrorEvent, EventSource } from 'eventsource';
import ky from 'ky';
import { useEffect, useRef, useState } from 'react';

type UseReportSseArgs = {
	authToken: string;
	baseUrl: string;
	jobId?: string;
};

type UseReportSseResult = {
	job: JobResponse | null;
	error: string | null;
	isConnected: boolean;
};

const parseJob = (event: MessageEvent<string>) => {
	return jobResponseSchema.parse(JSON.parse(event.data));
};

export const useReportSse = ({ authToken, baseUrl, jobId }: UseReportSseArgs): UseReportSseResult => {
	const [job, setJob] = useState<JobResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const activeJobIdRef = useRef<string | undefined>(jobId);

	useEffect(() => {
		activeJobIdRef.current = jobId;

		if (!jobId) {
			setJob(null);
			setError(null);
			setIsConnected(false);
			return;
		}

		setJob(null);
		setError(null);
		setIsConnected(false);

		const events = new EventSource(`${baseUrl}/jobs/${jobId}/events`, {
			fetch: ky.create({
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				timeout: false,
				retry: 0,
				throwHttpErrors: false,
			}),
		});

		const handleJobEvent = (event: MessageEvent<string>) => {
			try {
				const nextJob = parseJob(event);

				if (nextJob.jobId !== activeJobIdRef.current) {
					return false;
				}

				setJob(nextJob);
				return true;
			} catch {
				if (activeJobIdRef.current !== jobId) {
					return false;
				}

				setError('Received an invalid report status update.');
				setIsConnected(false);
				events.close();
				return false;
			}
		};

		events.addEventListener('open', () => {
			if (activeJobIdRef.current !== jobId) {
				events.close();
				return;
			}

			setIsConnected(true);
			setError(null);
		});

		events.addEventListener('job.updated', (event) => {
			handleJobEvent(event);
		});

		events.addEventListener('job.closed', (event) => {
			if (handleJobEvent(event)) {
				setIsConnected(false);
				events.close();
			}
		});

		events.addEventListener('error', (event: ErrorEvent) => {
			if (activeJobIdRef.current !== jobId) {
				events.close();
				return;
			}

			setIsConnected(false);
			setError(event.message);
		});

		return () => {
			events.close();
		};
	}, [authToken, baseUrl, jobId]);

	return { job, error, isConnected };
};
